import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import QRCode from "qrcode";
import { toPng } from "html-to-image";
import { Download, Share2, BadgeCheck, ShieldX } from "lucide-react";
import { useSEO } from "../components/SEO";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://fadosamesa.ch";

export default function CartaoMembroPage() {
    const { id } = useParams();
    const { t } = useLanguage();
    const c = (t.assoc && t.assoc.cartao) || {};
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(false);
    const [qr, setQr] = useState(null);
    const [aGuardar, setAGuardar] = useState(false);
    const cardRef = useRef(null);
    const url = `${SITE}/cartao-membro/${id}`;

    useSEO({
        title: "Cartão de Membro | Associação Fados à Mesa",
        description: "Cartão de membro digital — Associação Fados à Mesa.",
        robots: "noindex, nofollow",
    });

    useEffect(() => {
        let ativo = true;
        axios.get(`${API}/cartao-membro/${id}`)
            .then((r) => { if (ativo) setDados(r.data); })
            .catch(() => { if (ativo) setErro(true); });
        QRCode.toDataURL(url, { width: 480, margin: 1, color: { dark: "#070503", light: "#f5efe0" } })
            .then(setQr).catch(() => {});
        return () => { ativo = false; };
    }, [id, url]);

    const valido = !!(dados && dados.valido);
    const categoria = dados && (dados.categoria === "efetivo" ? c.categoriaEfetivo : c.categoriaApoiante);
    const desde = dados && dados.aprovado_em
        ? new Date(dados.aprovado_em).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "—";

    const guardarImagem = async () => {
        if (!cardRef.current) return;
        setAGuardar(true);
        try {
            const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: "#070503" });
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = "cartao-membro-fados-a-mesa.png";
            a.click();
        } finally {
            setAGuardar(false);
        }
    };

    const partilhar = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "Cartão de Membro — Fados à Mesa", url }); } catch { /* partilha cancelada */ }
        }
    };

    return (
        <div className="min-h-screen bg-[#070503] flex flex-col items-center justify-center px-5 py-10" data-testid="membro-page">
            {!dados && !erro && (
                <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#efe6d6]/50" data-testid="membro-a-carregar">{c.aCarregar || "…"}</p>
            )}

            {(erro || (dados && !dados.valido)) && (
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full max-w-sm border border-[#e0a0a0]/40 bg-[#0a0705] px-7 py-12 text-center"
                    data-testid="membro-invalido"
                >
                    <ShieldX size={28} className="text-[#e0a0a0] mx-auto" strokeWidth={1.4} />
                    <h1 className="font-display font-medium text-2xl text-[#efe6d6] mt-5" data-testid="membro-invalido-titulo">{c.invalidoTitulo}</h1>
                    <p className="font-light text-sm text-[#efe6d6]/60 mt-3 leading-relaxed">{c.invalidoTexto}</p>
                </motion.div>
            )}

            {valido && (
                <>
                    <motion.div
                        ref={cardRef}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="w-full max-w-sm border border-[#c9a24b]/35 bg-[#0a0705] px-7 py-9"
                        data-testid="membro-card"
                    >
                        <div className="flex flex-col items-center text-center">
                            <img src="/images/logo.webp" alt="Fados à Mesa" className="h-11 w-auto" data-testid="membro-logo" />
                            <p className="font-caps text-[10px] tracking-[0.35em] uppercase gold-text mt-5">{c.titulo}</p>
                            <div className="flex justify-center my-5">
                                <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                            </div>
                            <h1 className="font-display font-medium text-2xl text-[#efe6d6] tracking-wide" data-testid="membro-nome">{dados.nome}</h1>
                            <p className="font-caps text-[10px] tracking-[0.3em] uppercase gold-text mt-2.5" data-testid="membro-categoria">{categoria}</p>

                            <div className="flex items-start justify-between w-full mt-7 text-left">
                                <div>
                                    <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/45">{c.numero}</p>
                                    <p className="font-display text-lg text-[#efe6d6] mt-1" data-testid="membro-numero">{String(dados.membro_num || 0).padStart(4, "0")}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-caps text-[9px] tracking-[0.25em] uppercase text-[#efe6d6]/45">{c.desde}</p>
                                    <p className="font-display text-lg text-[#efe6d6] mt-1" data-testid="membro-desde">{desde}</p>
                                </div>
                            </div>

                            {qr && (
                                <div className="mt-7 bg-[#f5efe0] p-3" data-testid="membro-qr">
                                    <img src={qr} alt={`QR — ${url}`} className="w-36 h-36" />
                                </div>
                            )}
                            <p className="inline-flex items-center gap-2 font-caps text-[9px] tracking-[0.25em] uppercase text-[#9ec49a] mt-5" data-testid="membro-valido">
                                <BadgeCheck size={13} /> {c.valido}
                            </p>
                        </div>
                    </motion.div>

                    <p className="font-light text-xs text-[#efe6d6]/50 leading-relaxed text-center max-w-sm mt-6" data-testid="membro-nota">{c.nota}</p>

                    <div className="w-full max-w-sm mt-5 space-y-3">
                        <button onClick={guardarImagem} disabled={aGuardar} data-testid="membro-guardar"
                            className="w-full inline-flex items-center justify-center gap-2.5 font-caps text-[11px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-6 py-3.5 hover:bg-[#ecd49b] transition-colors duration-300 disabled:opacity-50">
                            <Download size={14} /> {c.guardar}
                        </button>
                        {typeof navigator !== "undefined" && !!navigator.share && (
                            <button onClick={partilhar} data-testid="membro-partilhar"
                                className="w-full inline-flex items-center justify-center gap-2.5 font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b] border border-[#c9a24b]/50 px-6 py-3.5 hover:bg-[#c9a24b]/10 transition-colors duration-300">
                                <Share2 size={14} /> {c.partilhar}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
